import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Package, Clock, CheckCircle, XCircle, Truck, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BuyerAPI, formatPrice, MessagingAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import OrderTimeline from '../components/OrderTimeline';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const MyOrders = () => {
  const contactSupplier = async (supplierId, orderId) => {
    try {
      const res = await MessagingAPI.startSupplier(supplierId, `order:${orderId}`, '');
      window.location.href = `/messages?c=${res.conversation_id}`;
    } catch (e) { /* silencieux */ }
  };
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusMap = {
    pending: { label: t('orders.status.pending'), icon: Clock, color: 'text-[#E0A52E] bg-[#FBF3DE]' },
    preparing: { label: t('orders.status.preparing'), icon: Package, color: 'text-blue-700 bg-blue-50' },
    shipped: { label: t('orders.status.shipped'), icon: Truck, color: 'text-[#1F4D3F] bg-[#E4EDE9]' },
    delivered: { label: t('orders.status.delivered'), icon: CheckCircle, color: 'text-[#2D6452] bg-[#DDEEE5]' },
    cancelled: { label: t('orders.status.cancelled'), icon: XCircle, color: 'text-[#C75B27] bg-[#F8E5D9]' },
  };

  useDocumentMeta({
    title: `${t('orders.title')} — Comptoir-Online`,
    description: "Suivez l'état de vos commandes de produits dentaires.",
  });

  useEffect(() => {
    if (!user) return;
    BuyerAPI.myOrders().then(setOrders).finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-[#5A5247]" data-testid="orders-login-required">{t('orders.login_required')}</div>;
  }

  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
        <Link to="/" className="hover:text-[#1F4D3F]">{t('common.account')}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">{t('orders.title')}</span>
      </nav>
      <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F] mb-6">{t('orders.title')}</h1>

      {loading ? (
        <div className="text-[#7A6F5C]" data-testid="orders-loading">{t('common.loading')}</div>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-12 text-center" data-testid="orders-empty">
          <div className="w-16 h-16 rounded-full bg-[#F5F1EA] flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-[#1F4D3F]" />
          </div>
          <h3 className="font-serif text-xl font-semibold text-[#1F4D3F] mb-1">Aucune commande pour le moment</h3>
          <p className="text-[#7A6F5C] mb-5">Parcourez notre catalogue et passez votre première commande en quelques clics.</p>
          <Link to="/categories" className="inline-flex items-center gap-2 bg-[#1F4D3F] hover:bg-[#173B30] text-white px-6 py-3 rounded-lg font-medium" data-testid="orders-browse-catalog-link">
            Parcourir le catalogue
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => {
            const st = statusMap[o.status] || statusMap.pending;
            const Icon = st.icon;
            return (
              <div key={o.id} className="bg-white border border-[#E8E0D0] rounded-2xl p-5" data-testid={`order-card-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[#F5F1EA]">
                  <div>
                    <div className="font-medium text-[#2D2A26]">{o.id}</div>
                    <div className="text-xs text-[#7A6F5C]">{new Date(o.created_at).toLocaleString(dateLocale)}</div>
                  </div>
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${st.color}`} data-testid={`order-status-badge-${o.id}`}>
                    <Icon className="w-3.5 h-3.5" /> {st.label}
                  </div>
                </div>

                <div className="px-1 sm:px-4 pb-2">
                  <OrderTimeline status={o.status} history={o.status_history || []} createdAt={o.created_at} />
                </div>

                <div className="py-3 space-y-2 border-t border-[#F5F1EA] mt-2">
                  {o.items.map((it) => (
                    <div key={`${it.product_id || it.name}-${it.variant_label}`} className="flex items-center gap-3 text-sm">
                      <img src={it.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-[#2D2A26]">{it.name}</div>
                        <div className="text-xs text-[#7A6F5C]">{it.variant_label} · x{it.qty}</div>
                      </div>
                      <div className="font-medium text-[#1F4D3F]">{formatPrice(it.subtotal)}</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-[#F5F1EA]">
                  <div className="text-xs text-[#7A6F5C]">{t('checkout.payment_cod')}</div>
                  <div className="font-serif text-lg font-semibold text-[#1F4D3F]" data-testid={`order-total-${o.id}`}>{t('cart.total')} : {formatPrice(o.total)}</div>
                </div>
                {(() => {
                  const supId = o.items?.[0]?.supplier_id;
                  return supId ? (
                    <button onClick={() => contactSupplier(supId, o.id)}
                      className="mt-3 w-full bg-white border border-[#1F4D3F] text-[#1F4D3F] hover:bg-[#1F4D3F] hover:text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-1.5 transition">
                      <MessageSquare className="w-4 h-4" /> Contacter le fournisseur
                    </button>
                  ) : null;
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
